// client/src/pages/ManageEmployees.jsx
import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Grid, TextField, Button, CircularProgress,
    Alert, Table, TableBody, TableCell, TableContainer, TableRow, TableHead,
    FormControl, InputLabel, Select, MenuItem, Avatar, Dialog, DialogTitle,
    DialogContent, DialogActions, IconButton, Chip, Tab, Tabs, Card, CardContent, Divider
} from '@mui/material';
import {
    BusinessCenter, Search, Edit, Visibility, Close,
    People, AdminPanelSettings, Computer, AccountBalance, Info,
    RefreshOutlined, DeleteOutline
} from '@mui/icons-material';
import { employeeService } from '../services/employeeService';

const ManageEmployees = () => {
    // Master registry states
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState('All');

    // Overlay states
    const [selectedEmp, setSelectedEmp] = useState(null);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [editForm, setEditForm] = useState({});
    const [targetEditId, setTargetEditId] = useState('');
    const [targetDeleteEmp, setTargetDeleteEmp] = useState(null);

    const fetchEmployees = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await employeeService.getAllEmployees();
            const dataPayload = response.data || response;
            setEmployees(Array.isArray(dataPayload) ? dataPayload : []);
        } catch (err) {
            setError('Failed to synchronize employee records from the database.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployees();
    }, []);

    const handleViewDetails = async (id) => {
        try {
            const profileData = await employeeService.getEmployeeProfile(id);
            const fullData = profileData.data || profileData;
            setSelectedEmp(fullData);
            setActiveTab(0);
            setViewDialogOpen(true);
        } catch (err) {
            console.error(err);
            alert('Failed to retrieve profile data.');
        }
    };

    const handleOpenEdit = async (id) => {
        try {
            const profileData = await employeeService.getEmployeeProfile(id);
            const fullData = profileData.data || profileData;
            setTargetEditId(id);

            setEditForm({
                id: fullData.id || '',
                name: fullData.name || '',
                email: fullData.email || '',
                phone: fullData.phone || '',
                role: fullData.role || 'BDE',
                password: fullData.password || '',
                dob: fullData.dob || '',
                home_address: fullData.home_address || '',
                aadhar_number_encrypted: fullData.aadhar_number_encrypted || '',
                pan_number: fullData.pan_number || '',
                job_role: fullData.job_role || '',
                reporting_manager: fullData.reporting_manager || '',
                office_location: fullData.office_location || '',
                date_of_joining: fullData.date_of_joining || '',
                date_of_exit: fullData.date_of_exit || '',
                previous_experience_years: fullData.previous_experience_years || '',
                education_qualification: fullData.education_qualification || '',
                bank_name: fullData.bank_name || '',
                account_number: fullData.account_number || '',
                ifsc_code: fullData.ifsc_code || '',
                salary_details: fullData.salary_details || '',
                corp_sim_number: fullData.corp_sim_number || '',
                laptop_serial_number: fullData.laptop_serial_number || '',
                profile_pic_url: fullData.profile_pic_url || '',
            });
            setEditDialogOpen(true);
        } catch (err) {
            console.error(err);
            alert('Failed to capture state configuration values.');
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateSubmit = async () => {
        try {
            if (!targetEditId) {
                alert("Target update context reference is missing.");
                return;
            }
            await employeeService.updateEmployee(targetEditId, editForm);
            setEditDialogOpen(false);
            fetchEmployees();
        } catch (err) {
            console.error(err);
            alert('Failed to push database update statement.');
        }
    };

    // Open delete warning overlay context
    const handleOpenDelete = (emp) => {
        setTargetDeleteEmp(emp);
        setDeleteDialogOpen(true);
    };

    // Handle profile permanent deletion routing
    const handleDeleteSubmit = async () => {
        try {
            if (!targetDeleteEmp || !targetDeleteEmp.id) return;
            // 🔥 FIXED: Renamed to match frontend employeeService mapping cleanly
            await employeeService.purgeEmployee(targetDeleteEmp.id);
            setDeleteDialogOpen(false);
            setTargetDeleteEmp(null);
            fetchEmployees(); // Synchronize view state layout
        } catch (err) {
            console.error(err);
            alert('Failed to purge employee profile record from architecture system databases.');
        }
    };

    const filteredEmployees = employees.filter(emp => {
        const matchesSearch = (emp.name?.toLowerCase().includes(searchQuery.toLowerCase())) ||
            String(emp.id)?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (emp.email?.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesRole = roleFilter === 'All' ||
            emp.role?.toLowerCase() === roleFilter.toLowerCase();

        return matchesSearch && matchesRole;
    });

    const adminHrCount = employees.filter(e => ['admin', 'hr'].includes(e.role?.toLowerCase())).length;
    const assetCount = employees.filter(e => e.laptop_serial_number || e.corp_sim_number).length;

    return (
        <Box sx={{ bgcolor: '#b0adab22', minHeight: '100vh', color: '#081c3b', pb: 2, display: 'flex', flexDirection: 'column' }}>

            {/* STICKY AUTHENTIC SALESFORCE LIGHTNING UTILITY HEADER BAR */}
            <Box sx={{
                position: 'sticky',
                top: 0,
                bgcolor: '#032d60',
                height: '55px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                color: '#ffffff',
                borderBottom: '3px solid #0176d3',
                zIndex: 1100
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{ bgcolor: '#fc8b22', p: 0.5, borderRadius: '4px', display: 'flex' }}>
                        <BusinessCenter sx={{ color: '#ffffff', fontSize: '18px' }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '15px', letterSpacing: '0.1px', fontFamily: 'Segoe UI, Roboto, Helvetica' }}>
                            Employee Directory
                        </Typography>
                        <Chip
                            label={`${filteredEmployees.length} items`}
                            size="small"
                            sx={{ bgcolor: '#014486', color: '#ffffff', fontWeight: 600, fontSize: '11px', height: '20px' }}
                        />
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    size="small"
                    startIcon={<RefreshOutlined />}
                    onClick={fetchEmployees}
                    sx={{ bgcolor: '#0176d3', color: '#ffffff', '&:hover': { bgcolor: '#014486' }, textTransform: 'none', borderRadius: '4px', fontWeight: 500, fontSize: '12px', border: '1px solid #0176d3' }}
                >
                    Refresh Registry
                </Button>
            </Box>

            {/* MAIN CONTAINER WINDOW PANEL */}
            <Box sx={{ pt: 2, px: 3, maxWidth: 1600, mx: 'auto', width: '100%', boxSizing: 'border-box', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>

                {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '4px' }}>{error}</Alert>}

                {/* SALESFORCE CARD SUMMARY COMPONENT MATRIX */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={0} sx={{ borderRadius: '4px', border: '1px solid #dddbda', bgcolor: '#ffffff' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', p: '16px !important', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#3ba755', color: '#ffffff', borderRadius: '4px', width: 40, height: 40 }}><People /></Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 400, fontSize: '12px' }}>Total Active Headcount</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#081c3b', fontSize: '18px' }}>{employees.length} Records</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={0} sx={{ borderRadius: '4px', border: '1px solid #dddbda', bgcolor: '#ffffff' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', p: '16px !important', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#a094ed', color: '#ffffff', borderRadius: '4px', width: 40, height: 40 }}><AdminPanelSettings /></Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 400, fontSize: '12px' }}>Privileged Security Contexts</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#081c3b', fontSize: '18px' }}>{adminHrCount} Active Admins</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card elevation={0} sx={{ borderRadius: '4px', border: '1px solid #dddbda', bgcolor: '#ffffff' }}>
                            <CardContent sx={{ display: 'flex', alignItems: 'center', p: '16px !important', gap: 2 }}>
                                <Avatar sx={{ bgcolor: '#1589ee', color: '#ffffff', borderRadius: '4px', width: 40, height: 40 }}><Computer /></Avatar>
                                <Box>
                                    <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 400, fontSize: '12px' }}>Hardware Infrastructure Nodes</Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#081c3b', fontSize: '18px' }}>{assetCount} Linked Hardware</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* LIGHTNING FILTER COMPONENT BAR */}
                <Paper elevation={0} sx={{ p: 1.5, mb: 2, bgcolor: '#f3f2f1', border: '1px solid #dddbda', borderRadius: '4px', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#ffffff', px: 1.5, py: 0.5, borderRadius: '4px', flex: 1, minWidth: '300px', border: '1px solid #e1e1e1' }}>
                        <Search sx={{ color: '#706e6b', fontSize: '18px' }} />
                        <TextField
                            variant="standard"
                            placeholder="Search this list by keyword, name, id..."
                            fullWidth
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            InputProps={{ disableUnderline: true, style: { fontSize: '13px', color: '#081c3b' } }}
                        />
                    </Box>
                    <FormControl size="small" sx={{ minWidth: 200, bgcolor: '#ffffff', borderRadius: '4px' }}>
                        <Select
                            value={roleFilter}
                            onChange={(e) => setRoleFilter(e.target.value)}
                            sx={{ fontSize: '13px', height: '32px', '.MuiOutlinedInput-notchedOutline': { borderColor: '#dddbda' } }}
                        >
                            <MenuItem value="All" sx={{ fontSize: '13px' }}>All Scoped Employee Roles</MenuItem>
                            <MenuItem value="Admin" sx={{ fontSize: '13px' }}>Admin Clearances</MenuItem>
                            <MenuItem value="HR" sx={{ fontSize: '13px' }}>HR Clearances</MenuItem>
                            <MenuItem value="BDE" sx={{ fontSize: '13px' }}>BDE Clearances</MenuItem>
                        </Select>
                    </FormControl>
                </Paper>

                {/* 🛠️ SCROLLER & STICKY IMPLEMENTATION WINDOW FRAMEWORK */}
                <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                        bgcolor: '#ffffff',
                        border: '1px solid #dddbda',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: 'calc(100vh - 290px)', // Enforces precise vertical scroll dimensions
                        position: 'relative'
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress size={28} sx={{ color: '#0176d3' }} /></Box>
                    ) : (
                        <Table size="small" stickyHeader sx={{ minWidth: 1100 }}>
                            <TableHead sx={{ bgcolor: '#fafaf9' }}>
                                <TableRow sx={{
                                    'th': {
                                        borderBottom: '1px solid #dddbda',
                                        py: 1,
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        color: '#514f4d',
                                        letterSpacing: '0.2px',
                                        bgcolor: '#fafaf9' // Essential override for seamless sticky row styling
                                    }
                                }}>
                                    <TableCell sx={{ pl: 2, width: '50px' }}></TableCell>
                                    <TableCell>ID</TableCell>
                                    <TableCell>NAME</TableCell>
                                    <TableCell>EMAIL</TableCell>
                                    <TableCell>SECURITY CONTEXT</TableCell>
                                    <TableCell>JOB ROLE</TableCell>
                                    <TableCell>OFFICE LOCATION</TableCell>
                                    <TableCell align="right" sx={{ pr: 2 }}>ACTIONS</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredEmployees.length === 0 ? (
                                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: '#706e6b', fontSize: '13px' }}>No records found matching criteria.</TableCell></TableRow>
                                ) : (
                                    filteredEmployees.map((row) => (
                                        <TableRow key={row.id} sx={{ '&:hover': { bgcolor: '#f3f2f1' }, 'td': { py: 1, fontSize: '13px', borderBottom: '1px solid #dddbda', color: '#16325c' } }}>
                                            <TableCell sx={{ pl: 2 }}>
                                                <Avatar src={row.profile_pic_url || ''} sx={{ width: 28, height: 28, borderRadius: '4px', border: '1px solid #dddbda' }} />
                                            </TableCell>
                                            <TableCell sx={{ color: '#706e6b' }}>{row.id}</TableCell>
                                            <TableCell
                                                onClick={() => handleViewDetails(row.id)}
                                                sx={{ fontWeight: 600, color: '#0176d3', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {row.name}
                                            </TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell>
                                                <Chip label={row.role || 'BDE'} size="small" sx={{ fontSize: '11px', height: '18px', fontWeight: 500, bgcolor: '#f3f2f1', color: '#514f4d', borderRadius: '3px', border: '1px solid #dddbda' }} />
                                            </TableCell>
                                            <TableCell>{row.job_role || '—'}</TableCell>
                                            <TableCell>{row.office_location || '—'}</TableCell>
                                            <TableCell align="right" sx={{ pr: 2 }}>
                                                <IconButton size="small" onClick={() => handleViewDetails(row.id)} sx={{ color: '#0176d3', p: 0.25 }} title="View Record"><Visibility fontSize="small" sx={{ fontSize: '16px' }} /></IconButton>
                                                <IconButton size="small" onClick={() => handleOpenEdit(row.id)} sx={{ color: '#706e6b', p: 0.25 }} title="Edit Record"><Edit fontSize="small" sx={{ fontSize: '16px' }} /></IconButton>
                                                <IconButton size="small" onClick={() => handleOpenDelete(row)} sx={{ color: '#c23934', p: 0.25 }} title="Delete Profile"><DeleteOutline fontSize="small" sx={{ fontSize: '16px' }} /></IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    )}
                </TableContainer>
            </Box>

            {/* ====== 1. LIGHTNING DETAILED VIEW MODAL ====== */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: '4px', border: '1px solid #cbd5e1' } }}>
                {selectedEmp && (
                    <>
                        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f3f2f1', borderBottom: '1px solid #dddbda', p: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Avatar src={selectedEmp.profile_pic_url || ""} sx={{ width: 44, height: 44, borderRadius: '4px', border: '1px solid #cbd5e1' }} />
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#081c3b', fontSize: '16px' }}>{selectedEmp.name}</Typography>
                                    <Typography variant="caption" sx={{ color: '#514f4d', display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, fontSize: '11px' }}>
                                        Employee ID: {selectedEmp.id} • Site: {selectedEmp.office_location || 'Remote Base Instance'}
                                    </Typography>
                                </Box>
                            </Box>
                            <IconButton onClick={() => setViewDialogOpen(false)} size="small" sx={{ bgcolor: '#ffffff', border: '1px solid #dddbda' }}><Close fontSize="small" /></IconButton>
                        </DialogTitle>

                        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: '1px solid #dddbda', px: 2, bgcolor: '#ffffff', minHeight: '36px', '.MuiTab-root': { minHeight: '36px', textTransform: 'none', fontWeight: 600, fontSize: '13px', color: '#514f4d' } }} indicatorColor="primary" textColor="primary">
                            <Tab label="Details" />
                            <Tab label="Organizational & HR Data" />
                            <Tab label="Financial Assets" />
                        </Tabs>

                        <DialogContent sx={{ p: 3, bgcolor: '#ffffff', maxHeight: '55vh' }}>
                            {activeTab === 0 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Email Address</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.email || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Phone Connection</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.phone || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Security Context Role</Typography><Typography variant="body2" sx={{ mt: 0.5 }}><Chip label={selectedEmp.role || 'BDE'} size="small" sx={{ borderRadius: '3px', height: '18px', fontSize: '11px' }} /></Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Date of Birth</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.dob || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Aadhaar Number (Encrypted)</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c', fontFamily: 'monospace' }}>[Aadhaar Redacted]</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>PAN Identifier</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c', fontFamily: 'monospace' }}>{selectedEmp.pan_number || '—'}</Typography></Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                                    <Grid item xs={12}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Home Residential Address</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.home_address || '—'}</Typography></Grid>
                                </Grid>
                            )}

                            {activeTab === 1 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Functional Title</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.job_role || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Reporting Manager</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.reporting_manager || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Office Location Base</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.office_location || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Date of Joining</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.date_of_joining || '—'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Date of Exit</Typography><Typography variant="body2" sx={{ mt: 0.5, color: selectedEmp.date_of_exit ? '#c23934' : '#04844b' }}>{selectedEmp.date_of_exit || 'Active Operator'}</Typography></Grid>
                                    <Grid item xs={12} sm={4}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Prior Corporate Experience</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.previous_experience_years ? `${selectedEmp.previous_experience_years} Years` : '—'}</Typography></Grid>
                                    <Grid item xs={12}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Highest Attained Qualification</Typography><Typography variant="body2" sx={{ mt: 0.5, color: '#16325c' }}>{selectedEmp.education_qualification || '—'}</Typography></Grid>
                                </Grid>
                            )}

                            {activeTab === 2 && (
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ p: 1.5, bgcolor: '#f3f2f1', borderRadius: '4px', borderLeft: '3px solid #0176d3' }}>
                                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}><AccountBalance sx={{ fontSize: '14px' }} /> Bank Registry</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, color: '#16325c' }}>{selectedEmp.bank_name || '—'}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ p: 1.5, bgcolor: '#f3f2f1', borderRadius: '4px', borderLeft: '3px solid #0176d3' }}>
                                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Account Number Reference</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, color: '#16325c' }}>{selectedEmp.account_number || '—'}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ p: 1.5, bgcolor: '#f3f2f1', borderRadius: '4px', borderLeft: '3px solid #0176d3' }}>
                                            <Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Routing Sorting IFSC</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25, color: '#16325c' }}>{selectedEmp.ifsc_code || '—'}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Assigned Compute Hardware ID</Typography><Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: '#0176d3' }}>{selectedEmp.laptop_serial_number || 'No System Allocated'}</Typography></Grid>
                                    <Grid item xs={12} sm={6}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Cellular Network SIM Number</Typography><Typography variant="body2" sx={{ fontWeight: 600, mt: 0.5, color: '#16325c' }}>{selectedEmp.corp_sim_number || 'No SIM Allocated'}</Typography></Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 0.5 }} /></Grid>
                                    <Grid item xs={12}><Typography variant="caption" sx={{ color: '#514f4d', fontWeight: 600 }}>Total Annual Scope Value (CTC)</Typography><Typography variant="h6" sx={{ fontWeight: 600, mt: 0.25, color: '#04844b' }}>{selectedEmp.salary_details ? `₹${Number(selectedEmp.salary_details).toLocaleString()}` : '—'}</Typography></Grid>
                                </Grid>
                            )}
                        </DialogContent>
                        <DialogActions sx={{ borderTop: '1px solid #dddbda', p: 2, bgcolor: '#f3f2f1' }}>
                            <Button variant="contained" size="small" onClick={() => setViewDialogOpen(false)} sx={{ textTransform: 'none', bgcolor: '#0176d3', px: 2, '&:hover': { bgcolor: '#014486' }, borderRadius: '4px' }}>Close Window</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* ====== 2. LIGHTNING COMPREHENSIVE EDIT DIALOG ====== */}
            <Dialog
                open={editDialogOpen}
                onClose={() => setEditDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '4px', maxHeight: '90vh', display: 'flex', flexDirection: 'column', border: '1px solid #cbd5e1' } }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f3f2f1', borderBottom: '1px solid #dddbda', p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#081c3b' }}>Edit Corporate Object Data Schema</Typography>
                        <Chip label="Fields Unlocked" size="small" color="warning" sx={{ height: '18px', fontSize: '10px', borderRadius: '3px' }} />
                    </Box>
                    <IconButton onClick={() => setEditDialogOpen(false)} size="small" sx={{ bgcolor: '#ffffff', border: '1px solid #dddbda' }}><Close fontSize="small" /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 3, overflowY: 'auto', flex: 1, bgcolor: '#ffffff' }}>
                    <Alert severity="warning" icon={<Info fontSize="small" />} sx={{ mb: 3, fontSize: '12px', borderRadius: '3px' }}>
                        Saving updates overwrites standard enterprise records inside production databases. Double check all attributes prior to synchronization.
                    </Alert>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0176d3', mb: 2, fontSize: '11px', letterSpacing: '0.5px' }}>IDENTITY CORE PARAMETERS</Typography>
                    <Grid container spacing={2.5} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Full Name" name="name" value={editForm.name || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Email Id" name="email" value={editForm.email || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Phone Number" name="phone" value={editForm.phone || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="edit-role-select-label" shrink>Access Authority Domain</InputLabel>
                                <Select labelId="edit-role-select-label" name="role" value={editForm.role || 'BDE'} label="Access Authority Domain" onChange={handleEditChange} displayEmpty>
                                    <MenuItem value="Admin">Admin</MenuItem>
                                    <MenuItem value="HR">HR</MenuItem>
                                    <MenuItem value="BDE">BDE</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Functional Job Title" name="job_role" value={editForm.job_role || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Office Base Location" name="office_location" value={editForm.office_location || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                    </Grid>

                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0176d3', mb: 2, fontSize: '11px', letterSpacing: '0.5px' }}>ADMINISTRATIVE FINANCIAL NODES</Typography>
                    <Grid container spacing={2.5}>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Reporting Manager" name="reporting_manager" value={editForm.reporting_manager || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Bank Association Corporate Name" name="bank_name" value={editForm.bank_name || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Disbursal Bank Account Number" name="account_number" value={editForm.account_number || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Assigned Laptop Serial Allocation" name="laptop_serial_number" value={editForm.laptop_serial_number || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                        <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Corporate SIM Provision Allocation" name="corp_sim_number" value={editForm.corp_sim_number || ''} onChange={handleEditChange} InputLabelProps={{ shrink: true }} /></Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ borderTop: '1px solid #dddbda', p: 2, bgcolor: '#f3f2f1' }}>
                    <Button variant="outlined" size="small" onClick={() => setEditDialogOpen(false)} sx={{ textTransform: 'none', color: '#514f4d', borderColor: '#dddbda', '&:hover': { bgcolor: '#ecebea', borderColor: '#dddbda' }, borderRadius: '4px' }}>Cancel</Button>
                    <Button variant="contained" size="small" onClick={handleUpdateSubmit} sx={{ textTransform: 'none', bgcolor: '#0176d3', px: 3, '&:hover': { bgcolor: '#014486' }, borderRadius: '4px' }}>Save Changes</Button>
                </DialogActions>
            </Dialog>

            {/* ====== 3. LIGHTNING PERMANENT DELETE SAFETY DESTRUCT OVERLAY ====== */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '4px', maxWidth: '440px' } }}>
                <DialogTitle sx={{ bgcolor: '#fff0f0', color: '#c23934', display: 'flex', alignItems: 'center', gap: 1, p: 2, borderBottom: '1px solid #f8d7da' }}>
                    <DeleteOutline /> Confirm Delete Employee Profile
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
                    <Typography variant="body2" sx={{ color: '#16325c', lineHeight: 1.6 }}>
                        Are you completely certain you want to permanently delete the profile context registry item matching employee <strong>{targetDeleteEmp?.name}</strong> (ID: {targetDeleteEmp?.id})?
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#c23934', bgcolor: '#fff5f5', p: 1, borderRadius: '3px', border: '1px solid #f8d7da', fontWeight: 500 }}>
                        ⚠️ ARCHITECTURAL WARNING: Wiping master architecture records drops child dependencies on spreadsheets instances permanently. This action is absolute.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: '#f3f2f1', borderTop: '1px solid #dddbda' }}>
                    <Button variant="outlined" size="small" onClick={() => setDeleteDialogOpen(false)} sx={{ textTransform: 'none', color: '#514f4d', borderColor: '#dddbda', borderRadius: '4px' }}>Cancel</Button>
                    <Button variant="contained" size="small" onClick={handleDeleteSubmit} sx={{ textTransform: 'none', bgcolor: '#c23934', '&:hover': { bgcolor: '#871e1a' }, borderRadius: '4px' }}>Delete Employee</Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default ManageEmployees;